import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { AuditHistory } from "@/components/audit/AuditHistory";
import { api } from "@/lib/apiClient";
import type { ArticleCategory, Discount, DiscountType } from "@/types/camping";

export interface EditDiscountModalProps {
  open: boolean;
  discount: Discount | null;
  onClose: () => void;
  onSaved: () => void;
  canEdit: boolean;
}

export function EditDiscountModal({
  open,
  discount,
  onClose,
  onSaved,
  canEdit,
}: EditDiscountModalProps) {
  const [form, setForm] = useState({
    name: "",
    discount_type: "percent_total" as DiscountType,
    value: "",
    article_category_id: "",
    valid_from: "",
    valid_until: "",
    is_active: true,
  });
  const [categories, setCategories] = useState<ArticleCategory[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !discount) return;
    setForm({
      name: discount.name,
      discount_type: discount.discount_type,
      value: discount.value,
      article_category_id: discount.article_category_id ?? "",
      valid_from: discount.valid_from ?? "",
      valid_until: discount.valid_until ?? "",
      is_active: discount.is_active,
    });
    setError(null);
    setSubmitting(false);
    api
      .get<ArticleCategory[]>("/api/v1/article-categories?include_inactive=true")
      .then(setCategories)
      .catch(() => setCategories([]));
  }, [open, discount]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!discount || !canEdit) return;
    setError(null);
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        name: form.name.trim(),
        discount_type: form.discount_type,
        value: form.value.trim(),
        is_active: form.is_active,
        valid_from: form.valid_from || null,
        valid_until: form.valid_until || null,
      };
      if (form.discount_type === "percent_category") {
        body.article_category_id = form.article_category_id || null;
      } else {
        body.article_category_id = null;
      }
      await api.patch(`/api/v1/discounts/${discount.id}`, body);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Speichern");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={discount?.name ?? "Rabatt"}
      description="Rabatt bearbeiten"
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <label className="flex flex-col gap-1 text-sm">
          Name
          <input
            required
            disabled={!canEdit}
            className="eh-input"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Rabattart
          <select
            disabled={!canEdit}
            className="eh-input"
            value={form.discount_type}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                discount_type: e.target.value as DiscountType,
              }))
            }
          >
            <option value="percent_total">Prozent auf Gesamt</option>
            <option value="percent_category">Prozent auf Artikelkategorie</option>
            <option value="fixed">Fixbetrag (EUR)</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Wert
          <input
            required
            disabled={!canEdit}
            type="number"
            min="0"
            step="0.01"
            max={form.discount_type === "fixed" ? undefined : "100"}
            className="eh-input w-40"
            value={form.value}
            onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
          />
        </label>
        {form.discount_type === "percent_category" ? (
          <label className="flex flex-col gap-1 text-sm">
            Artikelkategorie
            <select
              required
              disabled={!canEdit}
              className="eh-input"
              value={form.article_category_id}
              onChange={(e) =>
                setForm((f) => ({ ...f, article_category_id: e.target.value }))
              }
            >
              <option value="">Bitte wählen…</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm">
            Gültig ab
            <input
              type="date"
              disabled={!canEdit}
              className="eh-input"
              value={form.valid_from}
              onChange={(e) => setForm((f) => ({ ...f, valid_from: e.target.value }))}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Gültig bis
            <input
              type="date"
              disabled={!canEdit}
              className="eh-input"
              value={form.valid_until}
              onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))}
            />
          </label>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            disabled={!canEdit}
            checked={form.is_active}
            onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
          />
          Aktiv
        </label>
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        {discount ? (
          <AuditHistory entityType="discount" entityId={discount.id} />
        ) : null}
        <div className="eh-modal-actions flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Schließen
          </Button>
          {canEdit ? (
            <Button type="submit" loading={submitting}>
              Speichern
            </Button>
          ) : null}
        </div>
      </form>
    </Modal>
  );
}
